import { z } from "zod";

// Shared contracts for the internal-only "Integrations KB" feature: a
// per-partner tab in the admin detail view that live-fetches and renders a page
// from a single Notion database. Worker (apps/api) produces these DTOs; the SPA
// (apps/web) consumes them. The worker normalizes Notion's verbose block schema
// down to this small, render-ready shape — the SPA never sees raw Notion JSON.

// Body of POST /api/admin/integration-kb/:partnerId/pages — link a partner to a
// Notion page. We persist the page id (the live-fetch key) plus the url for the
// "Open in Notion" deep link.
export const linkNotionPageSchema = z.object({
  pageId: z.string().trim().min(1),
  pageUrl: z.string().trim().url().optional(),
});

export type LinkNotionPageInput = z.infer<typeof linkNotionPageSchema>;

// Body of PATCH /api/admin/integration-kb/:partnerId/pages/order — the new
// top-to-bottom order, identified by partner_notion_pages.id (linkId).
export const reorderNotionPagesSchema = z.object({
  orderedLinkIds: z.array(z.string().trim().min(1)).min(1),
});

export type ReorderNotionPagesInput = z.infer<typeof reorderNotionPagesSchema>;

// Inline text run with Notion's annotation flags flattened onto it.
export type NotionRichText = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  strikethrough?: boolean;
  underline?: boolean;
  code?: boolean;
  href?: string | null;
};

// Render-ready block. The worker maps the Notion block types we support; any
// other type collapses to `{ type: "unsupported", notionType }` so the renderer
// can show a graceful placeholder instead of crashing.
export type NotionBlock =
  | { type: "paragraph"; rich: NotionRichText[] }
  | { type: "heading_1"; rich: NotionRichText[] }
  | { type: "heading_2"; rich: NotionRichText[] }
  | { type: "heading_3"; rich: NotionRichText[] }
  | { type: "bulleted_list_item"; rich: NotionRichText[]; children: NotionBlock[] }
  | { type: "numbered_list_item"; rich: NotionRichText[]; children: NotionBlock[] }
  | { type: "to_do"; rich: NotionRichText[]; checked: boolean }
  | { type: "quote"; rich: NotionRichText[] }
  | { type: "callout"; rich: NotionRichText[]; emoji: string | null }
  | { type: "code"; rich: NotionRichText[]; language: string }
  | { type: "toggle"; rich: NotionRichText[]; children: NotionBlock[] }
  | { type: "divider" }
  | { type: "image"; url: string; caption: NotionRichText[] }
  | { type: "bookmark"; url: string; caption: NotionRichText[] }
  | { type: "table"; rows: NotionRichText[][][] }
  | { type: "unsupported"; notionType: string };

// A normalized database-row property. For this KB the structured value (status,
// contacts, API docs, markets…) lives in row properties, not the page body — so
// the worker flattens each populated Notion property to one of these. The page
// title property is excluded (it's surfaced as NotionPageDTO.title). Empty
// values are dropped server-side so the UI only renders populated fields.
export type NotionProperty =
  | { name: string; type: "text"; value: string }
  | { name: string; type: "url"; value: string }
  | { name: string; type: "email"; value: string }
  | { name: string; type: "phone"; value: string }
  | { name: string; type: "number"; value: number }
  | { name: string; type: "date"; value: string }
  | { name: string; type: "checkbox"; value: boolean }
  | { name: string; type: "select"; value: string }
  | { name: string; type: "multi_select"; value: string[] };

// A fully-resolved Notion page ready to render.
export type NotionPageDTO = {
  id: string;
  title: string;
  icon: string | null; // emoji glyph or icon image url
  url: string;
  lastEditedTime: string | null;
  properties: NotionProperty[];
  blocks: NotionBlock[];
  fetchedAt: string; // ISO; lets the UI show "as of <time>" + drive refresh
  truncated: boolean; // true if we hit the block/recursion cap
};

// One row of the link-picker (a page in the Integrations KB database).
export type NotionCandidateDTO = {
  id: string;
  title: string;
  icon: string | null;
  url: string;
  lastEditedTime: string | null;
};

// One linked Notion page in a partner's Knowledge Base. `page` is null when the
// live fetch for THIS page failed (Notion down, archived, no access) — `error`
// then carries a human-readable reason — so one bad page never breaks the rest.
export type PartnerKbPageDTO = {
  linkId: string; // partner_notion_pages.id — drives remove/reorder
  pageId: string;
  pageUrl: string | null;
  linkedAt: string;
  page: NotionPageDTO | null;
  error?: string;
};

// GET /api/admin/integration-kb/:partnerId response. `pages` is empty when the
// partner has nothing linked. Ordered by the partner's stored sort_order.
export type PartnerIntegrationKbDTO = {
  pages: PartnerKbPageDTO[];
};
